/**
 * The subset of JSON Schema an agent may use to declare its run input.
 *
 * Deliberately small. A full JSON Schema renderer is a project in itself and
 * most of the spec (oneOf, $ref, conditionals) has no sensible form control.
 * These are the shapes real agent inputs actually use, and anything outside
 * them falls back to the raw JSON editor rather than rendering a broken field.
 */

export type InputSchemaType = 'object' | 'array' | 'string' | 'number' | 'integer' | 'boolean';

export interface InputSchemaNode {
  type?: InputSchemaType;
  /** Field label. Falls back to a humanised version of the property key. */
  title?: string;
  /** Help text under the field. */
  description?: string;
  /** Placeholder / example value shown in an empty field. */
  examples?: unknown[];
  default?: unknown;
  enum?: string[];
  /** Render a multi-line box. Our own hint — not standard JSON Schema. */
  format?: 'textarea' | 'email' | 'uri' | string;
  properties?: Record<string, InputSchemaNode>;
  required?: string[];
  items?: InputSchemaNode;
  minimum?: number;
  maximum?: number;
}

export type InputSchema = InputSchemaNode & { type?: 'object' };

/** A schema we can actually render: an object with at least one property. */
export function isRenderableSchema(schema: unknown): schema is InputSchema {
  if (!schema || typeof schema !== 'object') return false;
  const s = schema as InputSchemaNode;
  if (s.type && s.type !== 'object') return false;
  return !!s.properties && Object.keys(s.properties).length > 0;
}

/** "target_boards" -> "Target boards". Used when a node has no title. */
export function humanise(key: string): string {
  const words = key.replace(/[_-]+/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * Build the empty value for a node, honouring `default`.
 *
 * Arrays start with one blank row rather than none — an empty repeatable
 * section reads as broken, and "add the first one" is an extra click on the
 * path every single user takes.
 */
export function emptyValueFor(node: InputSchemaNode): unknown {
  if (node.default !== undefined) return node.default;
  switch (node.type) {
    case 'object': {
      const out: Record<string, unknown> = {};
      for (const [key, child] of Object.entries(node.properties ?? {})) {
        out[key] = emptyValueFor(child);
      }
      return out;
    }
    case 'array':
      return node.items?.type === 'object' ? [emptyValueFor(node.items)] : [];
    case 'boolean':
      return false;
    case 'number':
    case 'integer':
      return '';
    default:
      return '';
  }
}

/** Seed form state from a schema, layered over any existing value. */
export function initialValue(
  schema: InputSchema,
  existing?: Record<string, unknown> | null
): Record<string, unknown> {
  const base = emptyValueFor({ ...schema, type: 'object' }) as Record<string, unknown>;
  if (!existing || typeof existing !== 'object') return base;
  return { ...base, ...existing };
}

/**
 * Drop empties before submitting.
 *
 * An agent reading `{"seeking": ""}` treats the key as answered-but-blank,
 * which is worse than absent — it stops asking and scores against nothing.
 */
export function pruneEmpty(value: unknown): unknown {
  if (Array.isArray(value)) {
    const rows = value.map(pruneEmpty).filter((v) => {
      if (v === undefined) return false;
      if (typeof v === 'object' && v !== null) return Object.keys(v).length > 0;
      return v !== '';
    });
    return rows.length ? rows : undefined;
  }
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [key, v] of Object.entries(value as Record<string, unknown>)) {
      const cleaned = pruneEmpty(v);
      if (cleaned !== undefined) out[key] = cleaned;
    }
    return Object.keys(out).length ? out : undefined;
  }
  if (value === '' || value === undefined || value === null) return undefined;
  return value;
}
